/**
 * Ícones SVG para marcadores de armário no mapa.
 * Cada ícone recebe uma cor (hex) e retorna o SVG.
 * Apenas 2 opções: 3 barras com quadrados (icon01) e 2 barras com círculos (icon02).
 */

export const ARMARIO_ICON_OPTIONS = [
  { id: 'icon01', label: '3 barras (quadrados)' },
  { id: 'icon02', label: '2 barras (círculos)' },
] as const;

export type ArmarioIconId = (typeof ARMARIO_ICON_OPTIONS)[number]['id'];

const DEFAULT_COLOR = '#FF0000';
const INNER_SHAPE_COLOR = '#000000'; // preto para contraste

/**
 * Ícone 1: Três retângulos horizontais empilhados, cada um com um quadrado preto.
 * Borda envolvente e 2 pés na base simulando um armário.
 */
function buildArmarioIcon01(color: string): string {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">' +
    // Borda do armário (moldura)
    '<rect x="8" y="8" width="84" height="78" rx="4" fill="none" stroke="' + color + '" stroke-width="3"/>' +
    // Barra 1
    '<rect x="14" y="14" width="72" height="18" rx="3" fill="' + color + '"/>' +
    '<rect x="22" y="19" width="10" height="8" fill="' + INNER_SHAPE_COLOR + '"/>' +
    // Barra 2
    '<rect x="14" y="38" width="72" height="18" rx="3" fill="' + color + '"/>' +
    '<rect x="22" y="43" width="10" height="8" fill="' + INNER_SHAPE_COLOR + '"/>' +
    // Barra 3
    '<rect x="14" y="62" width="72" height="18" rx="3" fill="' + color + '"/>' +
    '<rect x="22" y="67" width="10" height="8" fill="' + INNER_SHAPE_COLOR + '"/>' +
    // Pé esquerdo
    '<rect x="18" y="86" width="16" height="8" rx="2" fill="' + color + '"/>' +
    // Pé direito
    '<rect x="66" y="86" width="16" height="8" rx="2" fill="' + color + '"/>' +
    '</svg>'
  );
}

/**
 * Ícone 2: Dois retângulos horizontais empilhados, cada um com um círculo preto.
 * Borda envolvente e 2 pés na base simulando um armário.
 */
function buildArmarioIcon02(color: string): string {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">' +
    // Borda do armário (moldura)
    '<rect x="8" y="12" width="84" height="72" rx="4" fill="none" stroke="' + color + '" stroke-width="3"/>' +
    // Barra 1
    '<rect x="14" y="20" width="72" height="20" rx="3" fill="' + color + '"/>' +
    '<circle cx="34" cy="30" r="7" fill="' + INNER_SHAPE_COLOR + '"/>' +
    // Barra 2
    '<rect x="14" y="52" width="72" height="20" rx="3" fill="' + color + '"/>' +
    '<circle cx="34" cy="62" r="7" fill="' + INNER_SHAPE_COLOR + '"/>' +
    // Pé esquerdo
    '<rect x="18" y="86" width="16" height="8" rx="2" fill="' + color + '"/>' +
    // Pé direito
    '<rect x="66" y="86" width="16" height="8" rx="2" fill="' + color + '"/>' +
    '</svg>'
  );
}

function getSvgByIconId(iconId: string | null | undefined, color: string): string {
  const c = color && /^#[0-9A-Fa-f]{3,8}$/.test(color.trim()) ? color.trim() : DEFAULT_COLOR;
  switch (iconId) {
    case 'icon02':
      return buildArmarioIcon02(c);
    case 'icon01':
    default:
      return buildArmarioIcon01(c);
  }
}

export function getArmarioIconDataUrl(
  color: string | null | undefined,
  iconId?: string | null
): string {
  const svg = getSvgByIconId(iconId || 'icon01', color || DEFAULT_COLOR);
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}
